import React, { useEffect, useMemo, useState } from "react";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import {
  cancelAPIRequest,
  formatDate,
  generateArrayOfMonths,
  generateArrayOfYears,
  getDayNames,
} from "../../../../Utils";
import { useTranslation } from "react-i18next";
import {
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ExclamationCircleIcon,
  PlusIcon,
  SelectorIcon,
  XIcon,
} from "@heroicons/react/outline";
import {
  Field,
  Fieldset,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import clsx from "clsx";
import {
  addLocalNotification,
  clearBusinessCalendar,
  clearCalendarHoliday,
  requestCreateBusinessCalendar,
  requestDeleteCalendarHoliday,
  requestListBusinessCalendar,
  requestUpdateBusinessCalendar,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../../../Context";
import ErrorStateTable from "../../../../Components/ErrorStateTable";
import CalendarSkeleton from "../../../../Components/CalendarSkeleton";
import FormInput from "../../../../Components/v2/Form/FormInput";
import FormDate from "../../../../Components/v2/Form/FormDate";
import Button from "../../../../Components/v2/Button";
import FormError from "../../../../Components/v2/Form/FormError";
import HolidayModal from "../../../../Components/HolidayModal";
import { ModalAlert } from "../../../../Components/ModalAlert";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const Calendars = () => {
  const { t } = useTranslation();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [workDays, setWorkDays] = useState({
    0: false,
    1: true,
    2: true,
    3: true,
    4: true,
    5: true,
    6: false,
  });
  const [showForm, setShowForm] = useState(false);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    effectiveFrom: "",
    effectiveTo: "",
    workWeekPattern: {},
  });
  const [expandedCalendars, setExpandedCalendars] = useState({});
  const [calendarChanges, setCalendarChanges] = useState({});
  const [errors, setErrors] = useState({});

  const dispatch = useBusinessTransactionDispatch();
  const notificationDispatch = useNotificationDispatch();

  const { user } = useAuthState();
  const {
    request,
    calendars,
    calendarsLoading,
    calendarsError,
    newCalendar,
    newCalendarLoading,
    newCalendarError,
    updateCalendar,
    updateCalendarLoading,
    updateCalendarError,
    deletedHolidayId,
    deleteHolidayError,
    deleteHolidayLoading,
  } = useBusinessTransactionState();

  const today = new Date();

  const years = generateArrayOfYears();
  const months = generateArrayOfMonths();
  const dayNames = getDayNames();

  const currentDateString = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate.getFullYear(), currentDate.getMonth()]);

  const selectedMonth = useMemo(() => {
    const currentMonthIndex = currentDate.getMonth(); // 0-11
    return months.find(
      (month) => parseInt(month.value) === currentMonthIndex + 1,
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months, currentDate.getMonth()]);

  const selectedYear = useMemo(
    () => years.find((y) => y.value === currentDate.getFullYear()),
    [years, currentDate],
  );

  const isNextDisabled =
    new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      1,
    ).getFullYear() > today.getFullYear();
  const isPrevDisabled =
    new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
      1,
    ).getFullYear() <
    today.getFullYear() - 9;

  useEffect(() => {
    return () => {
      cancelAPIRequest(request);

      clearBusinessCalendar(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCalendars = () => {
    if (!user?.mainBusiness?.id) return;
    requestListBusinessCalendar(dispatch, {
      page: 1,
      limit: 10,
      businessID: user.mainBusiness.id,
      holidayDateFilter: { beginsWith: currentDateString },
    });
  };

  useEffect(() => {
    loadCalendars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.mainBusiness?.id, currentDateString]);

  //  Handle create success
  useEffect(() => {
    if (newCalendar?.id) {
      // Close modal
      handleClose();
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `create-calendar-${newCalendar?.id}`,
        type: "success",
        title: "common.success",
        body: "employees.calendars.notifications.createSuccess",
        showFull: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationDispatch, newCalendar?.id]);

  //  Handle update success
  useEffect(() => {
    if (updateCalendar?.id) {
      // Close modal
      handleClose();
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `update-calendar-${updateCalendar?.id}`,
        type: "success",
        title: "common.success",
        body: "employees.calendars.notifications.updateSuccess",
        showFull: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationDispatch, updateCalendar?.id]);

  //  Handle delete success
  useEffect(() => {
    if (deletedHolidayId) {
      // Close modal
      setShowConfirmModal(false);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `delete-calendar-holiday-${deletedHolidayId}`,
        type: "success",
        title: "common.success",
        body: "employees.holidays.notifications.deleteSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, deletedHolidayId]);

  useEffect(() => {
    if (deleteHolidayError) {
      setShowConfirmModal(false);
      setShowErrorModal(true);
    }
  }, [deleteHolidayError]);

  useEffect(() => {
    if (calendars && calendars.length > 0 && !calendarsLoading) {
      const newExpandedCalendars = {};
      calendars.forEach((calendar) => {
        newExpandedCalendars[calendar.id] = true;
      });
      setExpandedCalendars(newExpandedCalendars);
    }
  }, [calendars, calendarsLoading]);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePreviousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  const handleMonthChange = (month) => {
    const monthIndex = parseInt(month.value) - 1;
    setCurrentDate(new Date(currentDate.getFullYear(), monthIndex, 1));
  };

  const handleYearChange = (year) => {
    setCurrentDate(new Date(year.value, currentDate.getMonth(), 1));
  };

  const handleWorkDayToggle = (dayIndex) => {
    const newWorkDays = {
      ...workDays,
      [dayIndex.toString()]: !workDays[dayIndex.toString()],
    };

    setWorkDays(newWorkDays);

    // Update formData.workWeekPattern juga
    setFormData((prev) => ({
      ...prev,
      workWeekPattern: newWorkDays,
    }));
  };

  const isWorkDay = (dayIndex, pattern = null) => {
    if (pattern) {
      return pattern[dayIndex];
    }
    return workDays[dayIndex.toString()];
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
    );

    if (
      selectedDate &&
      selectedDate.getDate() === clickedDate.getDate() &&
      selectedDate.getMonth() === clickedDate.getMonth() &&
      selectedDate.getFullYear() === clickedDate.getFullYear()
    ) {
      setSelectedDate(null);
    } else {
      setSelectedDate(clickedDate);
    }
  };

  const isToday = (day) => {
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return (
      day === selectedDate.getDate() &&
      currentDate.getMonth() === selectedDate.getMonth() &&
      currentDate.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isSelectedDateWorkDay = (calendar) => {
    if (!selectedDate) return false;

    const dayOfWeek = selectedDate.getDay();
    const workPattern = getCalendarWorkPattern(calendar);

    return isWorkDay(dayOfWeek, workPattern);
  };

  const getHolidayInfo = (day) => {
    if (!calendars || calendars.length === 0) return null;

    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
    );

    const dateString = date.toLocaleDateString("en-CA");

    for (const calendar of calendars) {
      if (calendar.holidays?.items) {
        const holiday = calendar.holidays.items.find(
          (holiday) => holiday.date === dateString,
        );
        if (holiday) return holiday;
      }
    }

    return null;
  };

  const isHoliday = (day) => getHolidayInfo(day) !== null;
  const getHolidayName = (day) => getHolidayInfo(day)?.name || null;

  const getDayClass = (day, pattern = null) => {
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
    );
    const dayOfWeek = date.getDay();
    const isWorkingDay = isWorkDay(dayOfWeek, pattern);
    const isHolidayDay = isHoliday(day);
    const holidayInfo = getHolidayInfo(day);

    let baseClass =
      "h-16 border border-gray-200 flex items-center justify-center cursor-pointer transition-colors duration-200";

    if (isToday(day)) {
      return `${baseClass} bg-blue-600 text-white font-bold hover:bg-blue-700`;
    } else if (isSelected(day)) {
      return `${baseClass} bg-blue-100 text-blue-800 font-semibold border-blue-300`;
    } else if (isHolidayDay && holidayInfo && !holidayInfo.isPaid) {
      return `${baseClass} bg-yellow-50 text-yellow-800 hover:bg-yellow-100 border-yellow-200`;
    } else if (isHolidayDay) {
      return `${baseClass} bg-red-50 text-red-600 hover:bg-red-100 border-red-200`;
    } else if (isWorkingDay) {
      return `${baseClass} bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600`;
    } else {
      return `${baseClass} bg-red-50 text-red-600 hover:bg-red-100 border-red-200`;
    }
  };

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDayOfMonth = getFirstDayOfMonth(currentDate);

    return { daysInMonth, firstDayOfMonth };
  }, [currentDate]);

  const renderCalendarDays = (pattern = null) => {
    const { daysInMonth, firstDayOfMonth } = calendarDays;
    const days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          className="h-16 border border-gray-200 bg-gray-50"
        ></div>,
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(
        <div
          key={day}
          onClick={() => handleDateClick(day)}
          className={getDayClass(day, pattern)}
        >
          <span className="text-sm">{day}</span>
        </div>,
      );
    }

    return days;
  };

  const formatSelectedDate = () => {
    if (!selectedDate) return null;
    return selectedDate.toLocaleDateString("id-ID", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const calendarData = {
      businessID: user.mainBusiness.id,
      name: formData.name,
      effectiveFrom: formatDate(formData.effectiveFrom),
      effectiveTo: formData.effectiveTo
        ? formatDate(formData.effectiveTo)
        : null,
      workWeekPattern: JSON.stringify(formData.workWeekPattern),
    };

    requestCreateBusinessCalendar(dispatch, { input: calendarData });
  };

  const handleUpdateCalendar = (calendarId) => {
    const changes = calendarChanges[calendarId];
    if (!changes) return;

    const updateData = {
      id: calendarId,
      businessID: user.mainBusiness.id,
      name: changes.name,
      workWeekPattern: changes.workWeekPattern,
      effectiveFrom: changes.effectiveFrom,
      effectiveTo: changes.effectiveTo || null,
    };

    requestUpdateBusinessCalendar(dispatch, { input: updateData });
  };

  const handleCreateCalendar = () => {
    setShowForm(true);
    setFormData({
      name: "",
      effectiveFrom: "",
      effectiveTo: "",
      workWeekPattern: workDays,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = t("employees.calendars.errors.nameRequired");
    }

    if (!formData.effectiveFrom) {
      newErrors.effectiveFrom = t(
        "employees.calendars.errors.effectiveFromRequired",
      );
    }

    const workDaysSelected = Object.values(formData.workWeekPattern).filter(
      Boolean,
    ).length;
    if (workDaysSelected === 0) {
      newErrors.workWeekPattern = t(
        "employees.calendars.errors.workDayRequired",
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleCalendarExpansion = (calendarId) => {
    setExpandedCalendars((prev) => ({
      ...prev,
      [calendarId]: !prev[calendarId],
    }));
  };

  const handleCalendarWorkDayChange = (calendarId, dayIndex) => {
    const calendar = calendars.find((c) => c.id === calendarId);
    if (!calendar) return;

    const currentPattern = getCalendarWorkPattern(calendar);
    const newPattern = {
      ...currentPattern,
      [dayIndex]: !currentPattern[dayIndex],
    };

    setCalendarChanges((prev) => ({
      ...prev,
      [calendarId]: {
        ...(prev[calendarId] || calendar),
        workWeekPattern: JSON.stringify(newPattern),
      },
    }));
  };

  const hasCalendarChanges = (calendarId) => {
    return calendarChanges[calendarId] !== undefined;
  };

  const getCalendarWorkPattern = (calendar) => {
    const changes = calendarChanges[calendar.id];
    if (changes) {
      return JSON.parse(changes.workWeekPattern);
    }
    return JSON.parse(calendar.workWeekPattern);
  };

  const renderEmptyState = () => (
    <div className="text-center py-16">
      <CalendarIcon className="mx-auto h-16 w-16 text-gray-400" />
      <h3 className="mt-4 text-lg font-medium text-gray-900">
        {t("employees.calendars.empty.title")}
      </h3>
      <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
        {t("employees.calendars.empty.description")}
      </p>
    </div>
  );

  const renderCalendarForm = () => (
    <div className="bg-white border-b border-gray-200 px-4 py-5 sm:px-6 lg:px-8 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          {t("employees.calendars.header.create")}
        </h3>
        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <XIcon className="h-5 w-5" />
        </button>
      </div>

      <form onSubmit={handleFormSubmit} className="space-y-6">
        <FormError error={newCalendarError} />
        <Fieldset disabled={newCalendarLoading} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field>
              <FormInput
                required
                label="employees.calendars.fields.name"
                id="name"
                name="name"
                value={formData.name}
                placeholder={t("employees.calendars.placeholders.name")}
                onChange={(e) => handleFormChange("name", e.target.value)}
                error={errors.name}
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field>
                <FormDate
                  required
                  label="employees.calendars.fields.effectiveFrom"
                  name="effectiveFrom"
                  selected={formData.effectiveFrom || null}
                  onChange={(option) =>
                    handleFormChange("effectiveFrom", option)
                  }
                  placeholderText={t(
                    "employees.calendars.placeholders.effectiveFrom",
                  )}
                  dateFormat="dd/MM/yyyy"
                  showMonthDropdown
                  useShortMonthInDropdown
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={70}
                  maxDate={today}
                  error={errors.effectiveFrom}
                />
              </Field>

              <Field>
                <FormDate
                  label="employees.calendars.fields.effectiveTo"
                  name="effectiveTo"
                  selected={formData.effectiveTo || null}
                  onChange={(option) => handleFormChange("effectiveTo", option)}
                  placeholderText={t(
                    "employees.calendars.placeholders.effectiveTo",
                  )}
                  dateFormat="dd/MM/yyyy"
                  onClear={() => handleFormChange("effectiveTo", null)}
                  showMonthDropdown
                  useShortMonthInDropdown
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={70}
                  minDate={
                    formData.effectiveTo
                      ? new Date(formData.effectiveTo.getTime() + 86400000)
                      : today
                  }
                  hint={t("common.optional")}
                />
              </Field>
            </div>
          </div>

          {/* Calendar Preview Section */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            {/* Month/Year Navigation */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
              <button
                type="button"
                onClick={handlePreviousMonth}
                disabled={isPrevDisabled}
                className={`p-2 rounded-lg transition-colors duration-200 ${
                  isPrevDisabled
                    ? "cursor-not-allowed opacity-50"
                    : "hover:bg-gray-200"
                }`}
              >
                <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
              </button>

              <div className="flex items-center gap-4">
                {/* Month Selector */}
                <div>
                  <Listbox value={selectedMonth} onChange={handleMonthChange}>
                    {({ open }) => (
                      <>
                        <ListboxButton className="flex items-center justify-between w-full sm:w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm">
                          <span className="max-w-full mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                            {t(selectedMonth?.label || "Month")}
                          </span>
                          <SelectorIcon className="w-5 h-5 text-gray-500" />
                        </ListboxButton>
                        <Transition
                          show={open}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 sm:w-fit rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                            {months.map((month) => (
                              <ListboxOption
                                key={month.value}
                                className={({ focus }) =>
                                  clsx(
                                    focus
                                      ? "text-white bg-blue-600"
                                      : "text-gray-900",
                                    "cursor-default select-none relative py-2 pr-9",
                                  )
                                }
                                value={month}
                              >
                                {({ selected, focus }) => (
                                  <>
                                    <div className="flex items-center justify-between">
                                      <span
                                        className={clsx(
                                          selected
                                            ? "font-semibold"
                                            : "font-normal",
                                          "ml-3 mr-2 block truncate",
                                        )}
                                      >
                                        {t(month.label)}
                                      </span>
                                      {selected ? (
                                        <span
                                          className={clsx(
                                            focus
                                              ? "text-white"
                                              : "text-blue-600",
                                            "absolute inset-y-0 right-0 flex items-center pr-4",
                                          )}
                                        >
                                          <CheckIcon className="h-5 w-5" />
                                        </span>
                                      ) : null}
                                    </div>
                                  </>
                                )}
                              </ListboxOption>
                            ))}
                          </ListboxOptions>
                        </Transition>
                      </>
                    )}
                  </Listbox>
                </div>
                {/* Year Selector */}
                <div>
                  <Listbox value={selectedYear} onChange={handleYearChange}>
                    {({ open }) => (
                      <>
                        <ListboxButton className="flex items-center justify-between w-full sm:w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm">
                          <span className="max-w-full mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                            {t(selectedYear?.label || "Year")}
                          </span>
                          <SelectorIcon className="w-5 h-5 text-gray-500" />
                        </ListboxButton>
                        <Transition
                          show={open}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 sm:w-fit rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                            {years.map((year) => (
                              <ListboxOption
                                key={year.value}
                                className={({ focus }) =>
                                  clsx(
                                    focus
                                      ? "text-white bg-blue-600"
                                      : "text-gray-900",
                                    "cursor-default select-none relative py-2 pr-9",
                                  )
                                }
                                value={year}
                              >
                                {({ selected, focus }) => (
                                  <>
                                    <div className="flex items-center justify-between">
                                      <span
                                        className={clsx(
                                          selected
                                            ? "font-semibold"
                                            : "font-normal",
                                          "ml-3 mr-2 block truncate",
                                        )}
                                      >
                                        {t(year.label)}
                                      </span>
                                      {selected ? (
                                        <span
                                          className={clsx(
                                            focus
                                              ? "text-white"
                                              : "text-blue-600",
                                            "absolute inset-y-0 right-0 flex items-center pr-4",
                                          )}
                                        >
                                          <CheckIcon className="h-5 w-5" />
                                        </span>
                                      ) : null}
                                    </div>
                                  </>
                                )}
                              </ListboxOption>
                            ))}
                          </ListboxOptions>
                        </Transition>
                      </>
                    )}
                  </Listbox>
                </div>
              </div>

              <button
                onClick={handleNextMonth}
                disabled={isNextDisabled}
                className={`p-2 rounded-lg transition-colors duration-200 ${
                  isNextDisabled
                    ? "cursor-not-allowed opacity-50"
                    : "hover:bg-gray-200"
                }`}
              >
                <ChevronRightIcon className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="bg-white rounded-lg p-4">
              <div className="grid grid-cols-7 mb-2">
                {dayNames.map((day) => (
                  <div
                    key={day}
                    className="h-4 flex items-center justify-center"
                  >
                    <span className="text-xs font-medium text-gray-500">
                      {day}
                    </span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-0 border border-gray-200">
                {renderCalendarDays(formData.workWeekPattern)}
              </div>
              {selectedDate && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-sm text-blue-900 mb-1">
                    {t("employees.calendars.selectedDate")}
                  </div>
                  <p className="text-sm font-semibold text-blue-900">
                    {formatSelectedDate()}
                  </p>
                </div>
              )}

              {/* Calendar Legends */}
              <div className="mt-4 grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 bg-blue-600 rounded flex-shrink-0"></div>
                  <span>{t("employees.calendars.legends.today")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 bg-blue-100 border border-blue-200 rounded flex-shrink-0"></div>
                  <span>{t("employees.calendars.legends.selected")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white border border-gray-200 rounded flex-shrink-0"></div>
                  <span>{t("employees.calendars.legends.workDay")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 bg-red-50 border border-red-200 rounded flex-shrink-0"></div>
                  <span>{t("employees.calendars.legends.holiday")}</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("employees.calendars.fields.calendar")} *
            </label>
            <div className="grid grid-cols-7 gap-2">
              {dayNames.map((dayName, index) => (
                <label
                  key={index}
                  className="flex flex-col items-center space-y-1 text-xs"
                >
                  <input
                    type="checkbox"
                    checked={formData.workWeekPattern[index] || false}
                    onChange={() => handleWorkDayToggle(index)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span
                    className={
                      formData.workWeekPattern[index]
                        ? "text-gray-900"
                        : "text-red-600"
                    }
                  >
                    {dayName}
                  </span>
                </label>
              ))}
            </div>
            {errors.workWeekPattern && (
              <p className="text-red-600 text-sm mt-1">
                {t("employees.calendars.errors.workDayRequired")}
              </p>
            )}
          </div>
        </Fieldset>

        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="secondary" onClick={handleClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={newCalendarLoading}
            loading={newCalendarLoading}
          >
            {t("common.save")}
          </Button>
        </div>
      </form>
    </div>
  );

  const renderCalendarList = () => (
    <div className="space-y-4">
      {calendars.map((calendar) => {
        const isExpanded = expandedCalendars[calendar.id];
        const workPattern = getCalendarWorkPattern(calendar);
        const hasChanges = hasCalendarChanges(calendar.id);

        return (
          <div key={calendar.id} className="border-b border-gray-200">
            <div
              className="px-4 py-5 sm:px-6 lg:px-8 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleCalendarExpansion(calendar.id)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {calendar.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {t("employees.calendars.fields.effectiveFrom")}:{" "}
                    {calendar.effectiveFrom}
                    {calendar.effectiveTo && (
                      <span> - {calendar.effectiveTo}</span>
                    )}
                  </p>
                </div>
                {isExpanded ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <div className="flex items-center justify-end gap-2">
                    {updateCalendarError && (
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                    )}
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>

            {isExpanded && (
              <div className="border-t border-gray-200">
                {/* Calendar View Section */}
                <div className="bg-gray-50 px-4 py-5 sm:px-6 lg:px-8 border-t border-gray-200">
                  <FormError className="mb-4" error={updateCalendarError} />
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4 px-2">
                    <button
                      type="button"
                      onClick={handlePreviousMonth}
                      disabled={isPrevDisabled}
                      className={`p-2 rounded-lg transition-colors duration-200 ${
                        isPrevDisabled
                          ? "cursor-not-allowed opacity-50"
                          : "hover:bg-gray-200"
                      }`}
                    >
                      <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
                    </button>

                    <div className="flex items-center gap-4">
                      {/* Month Selector */}
                      <div>
                        <Listbox
                          value={selectedMonth}
                          onChange={handleMonthChange}
                        >
                          {({ open }) => (
                            <>
                              <ListboxButton className="flex items-center justify-between w-full sm:w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm">
                                <span className="max-w-full mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                                  {t(selectedMonth?.label || "Month")}
                                </span>
                                <SelectorIcon className="w-5 h-5 text-gray-500" />
                              </ListboxButton>
                              <Transition
                                show={open}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                              >
                                <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 sm:w-fit rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                                  {months.map((month) => (
                                    <ListboxOption
                                      key={month.value}
                                      className={({ focus }) =>
                                        clsx(
                                          focus
                                            ? "text-white bg-blue-600"
                                            : "text-gray-900",
                                          "cursor-default select-none relative py-2 pr-9",
                                        )
                                      }
                                      value={month}
                                    >
                                      {({ selected, focus }) => (
                                        <>
                                          <div className="flex items-center justify-between">
                                            <span
                                              className={clsx(
                                                selected
                                                  ? "font-semibold"
                                                  : "font-normal",
                                                "ml-3 mr-2 block truncate",
                                              )}
                                            >
                                              {t(month.label)}
                                            </span>
                                            {selected ? (
                                              <span
                                                className={clsx(
                                                  focus
                                                    ? "text-white"
                                                    : "text-blue-600",
                                                  "absolute inset-y-0 right-0 flex items-center pr-4",
                                                )}
                                              >
                                                <CheckIcon className="h-5 w-5" />
                                              </span>
                                            ) : null}
                                          </div>
                                        </>
                                      )}
                                    </ListboxOption>
                                  ))}
                                </ListboxOptions>
                              </Transition>
                            </>
                          )}
                        </Listbox>
                      </div>
                      {/* Year Selector */}
                      <div>
                        <Listbox
                          value={selectedYear}
                          onChange={handleYearChange}
                        >
                          {({ open }) => (
                            <>
                              <ListboxButton className="flex items-center justify-between w-full sm:w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm">
                                <span className="max-w-full mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                                  {t(selectedYear?.label || "Year")}
                                </span>
                                <SelectorIcon className="w-5 h-5 text-gray-500" />
                              </ListboxButton>
                              <Transition
                                show={open}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                              >
                                <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 sm:w-fit rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                  {years.map((year) => (
                                    <ListboxOption
                                      key={year.value}
                                      className={({ focus }) =>
                                        clsx(
                                          focus
                                            ? "text-white bg-blue-600"
                                            : "text-gray-900",
                                          "cursor-default select-none relative py-2 pr-9",
                                        )
                                      }
                                      value={year}
                                    >
                                      {({ selected, focus }) => (
                                        <>
                                          <div className="flex items-center justify-between">
                                            <span
                                              className={clsx(
                                                selected
                                                  ? "font-semibold"
                                                  : "font-normal",
                                                "ml-3 mr-2 block truncate",
                                              )}
                                            >
                                              {t(year.label)}
                                            </span>
                                            {selected ? (
                                              <span
                                                className={clsx(
                                                  focus
                                                    ? "text-white"
                                                    : "text-blue-600",
                                                  "absolute inset-y-0 right-0 flex items-center pr-4",
                                                )}
                                              >
                                                <CheckIcon className="h-5 w-5" />
                                              </span>
                                            ) : null}
                                          </div>
                                        </>
                                      )}
                                    </ListboxOption>
                                  ))}
                                </ListboxOptions>
                              </Transition>
                            </>
                          )}
                        </Listbox>
                      </div>
                    </div>

                    <button
                      onClick={handleNextMonth}
                      disabled={isNextDisabled}
                      className={`p-2 rounded-lg transition-colors duration-200 ${
                        isNextDisabled
                          ? "cursor-not-allowed opacity-50"
                          : "hover:bg-gray-200"
                      }`}
                    >
                      <ChevronRightIcon className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <div className="grid grid-cols-7 mb-2">
                      {dayNames.map((day) => (
                        <div
                          key={day}
                          className="h-4 flex items-center justify-center"
                        >
                          <span className="text-xs font-medium text-gray-500">
                            {day}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-0 border border-gray-200">
                      {renderCalendarDays(workPattern)}
                    </div>
                    {selectedDate && (
                      <div className="flex items-center justify-between mt-4 sm:mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200 gap-4">
                        <div>
                          <div className="text-sm text-blue-900 mb-1">
                            {t("employees.calendars.selectedDate")}
                          </div>
                          <p className="text-sm font-semibold text-blue-900">
                            {formatSelectedDate()}
                          </p>
                          {isHoliday(selectedDate.getDate()) && (
                            <p className="text-sm font-semibold text-red-600 mt-1">
                              {getHolidayName(selectedDate.getDate(), calendar)}
                            </p>
                          )}
                        </div>
                        {isHoliday(selectedDate.getDate()) && (
                          <Button
                            color="red"
                            onClick={() => {
                              const holidayInfo = getHolidayInfo(
                                selectedDate.getDate(),
                              );
                              if (holidayInfo) {
                                handleDelete(holidayInfo);
                              }
                            }}
                          >
                            {t("common.delete")}
                          </Button>
                        )}
                        {!isHoliday(selectedDate.getDate()) &&
                          isSelectedDateWorkDay(calendar) && (
                            <Button
                              type="button"
                              onClick={() => {
                                setSelectedCalendar(calendar);
                                setShowHolidayModal(true);
                              }}
                            >
                              {t("employees.holidays.modal.title.add")}
                            </Button>
                          )}
                      </div>
                    )}
                    <div className="mt-4 sm:mt-6 grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-blue-600 rounded flex-shrink-0"></div>
                        <span>{t("employees.calendars.legends.today")}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-blue-100 border border-blue-200 rounded flex-shrink-0"></div>
                        <span>{t("employees.calendars.legends.selected")}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white border border-gray-200 rounded flex-shrink-0"></div>
                        <span>{t("employees.calendars.legends.workDay")}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-red-50 border border-red-200 rounded flex-shrink-0"></div>
                        <span>{t("employees.calendars.legends.holiday")}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-yellow-50 border border-yellow-200 rounded flex-shrink-0"></div>
                        <span>
                          {t("employees.calendars.legends.unpaidHoliday")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-white rounded-lg border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      {t("employees.calendars.workDaySettings")}
                    </h4>
                    <div className="grid grid-cols-7 gap-2 mb-4">
                      {dayNames.map((dayName, index) => (
                        <label
                          key={index}
                          className="flex flex-col items-center space-y-1 text-xs"
                        >
                          <input
                            type="checkbox"
                            checked={workPattern[index]}
                            onChange={() =>
                              handleCalendarWorkDayChange(calendar.id, index)
                            }
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span
                            className={
                              workPattern[index]
                                ? "text-gray-900"
                                : "text-red-600"
                            }
                          >
                            {dayName}
                          </span>
                        </label>
                      ))}
                    </div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      {t("employees.calendars.workDayStatus")}:
                    </h4>
                    <div className="mb-6 text-xs text-gray-600 space-y-1">
                      <p>
                        {t("employees.calendars.workingDays")}:{" "}
                        <span className="font-medium">
                          {t("employees.calendars.daysCount", {
                            count:
                              Object.values(workPattern).filter(Boolean).length,
                          })}
                        </span>
                      </p>
                      <p>
                        {t("employees.calendars.holidays")}:{" "}
                        <span className="font-medium">
                          {t("employees.calendars.daysCount", {
                            count: Object.values(workPattern).filter(
                              (day) => !day,
                            ).length,
                          })}
                        </span>
                      </p>
                    </div>

                    <Button
                      loading={updateCalendarLoading}
                      disabled={!hasChanges || updateCalendarLoading}
                      onClick={() => handleUpdateCalendar(calendar.id)}
                      variant="primary"
                    >
                      {t("employees.calendars.header.edit")}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const handleDelete = (holiday) => {
    setSelectedCalendar(holiday);
    setShowConfirmModal(true);
  };

  const onDelete = () => {
    requestDeleteCalendarHoliday(dispatch, {
      id: selectedCalendar?.id,
    });
  };

  const handleClose = () => {
    if (request) {
      cancelAPIRequest(request);
    }
    clearBusinessCalendar(dispatch);
    setShowForm(false);
    setErrors({});
    setSelectedDate(null);
    setSelectedCalendar(null);
    setShowHolidayModal(false);
  };

  const handleCloseDelete = () => {
    if (request) {
      cancelAPIRequest(request);
    }
    setShowConfirmModal(false);
    setShowErrorModal(false);
    clearBusinessCalendar(dispatch);
    setSelectedCalendar(null);
  };

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader
          title="employees.calendars.title"
          primaryAction={
            // A failed load is not "no calendar yet": offering to create one
            // there would invite a duplicate.
            !calendarsLoading && !calendarsError && calendars.length === 0
              ? {
                  label: "employees.calendars.header.create",
                  icon: <PlusIcon className="h-5 w-5" />,
                  onClick: handleCreateCalendar,
                }
              : undefined
          }
        />

        {showForm && renderCalendarForm()}

        {calendarsLoading ? (
          <CalendarSkeleton />
        ) : calendars.length > 0 ? (
          renderCalendarList()
        ) : calendarsError ? (
          // The month navigation lives inside the calendar list, so a failed
          // month change leaves only this retry to get back.
          <div className="p-4">
            <div className="p-4 border border-gray-200 rounded-lg">
              <ErrorStateTable
                asRow={false}
                error={calendarsError}
                onRetry={loadCalendars}
              />
            </div>
          </div>
        ) : (
          renderEmptyState()
        )}
      </main>

      {/* Holiday Modal */}
      <HolidayModal
        open={showHolidayModal}
        onClose={handleClose}
        selectedDate={selectedDate}
        calendar={selectedCalendar}
      />

      {/* Delete Modal */}
      <ModalAlert
        open={showConfirmModal}
        setOpen={(open) => {
          setShowConfirmModal(open);
        }}
        title="employees.holidays.modal.title.delete"
        description="employees.holidays.modal.description.delete"
        confirmButtonText="Yes"
        cancelButtonText="Cancel"
        loadingButton={deleteHolidayLoading}
        disabledButton={deleteHolidayLoading}
        onConfirm={onDelete}
        onCancel={handleCloseDelete}
      />

      {/* Error Modal */}
      {deleteHolidayError && (
        <ErrorDialog
          open={showErrorModal}
          error={deleteHolidayError}
          onClose={() => {
            setShowErrorModal(false);
            clearCalendarHoliday(dispatch);
          }}
        />
      )}
    </AccountingWrapper>
  );
};

export default Calendars;
